import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Slider } from 'antd';
import { Button, SparkIcon, Spinner, TooltipTrigger, sparkToast } from '@spark';
import CrownIcon from '@/shared/assets/icons/crown_icon.svg?react';
import BoltIcon from '@/shared/assets/icons/bolt_icon.svg?react';
import {
    ComposedChart,
    LineChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    ReferenceLine,
    ReferenceDot,
} from 'recharts';
import { data_from_server_post } from '@/shared/http/client';
import {
    falsePositiveRate,
    parseUnitInterval,
    sanitizeDecimalInput,
    thresholdForFpr,
} from '../utils/thresholdFpr';

const DEFAULT_THRESHOLD = 0.5;

// Thresholds dragged to (or past) these edges trade off heavily toward
// precision (high) or recall (low), so we surface a warning to the user.
const HIGH_THRESHOLD_EDGE = 0.95;
const LOW_THRESHOLD_EDGE = 0.05;

// Chart zoom bounds. ZOOM_MIN keeps the full [0, 1] view; each +/- step scales by
// ZOOM_FACTOR up to ZOOM_MAX (~10% of the full range visible at max zoom).
const ZOOM_MIN = 1;
const ZOOM_MAX = 10;
const ZOOM_FACTOR = 1.5;

// Figma design tokens (5325-9288).
const TEXT_PRIMARY = '#1a1b23';
const TEXT_SECONDARY = '#555768';
const BORDER = '#e2e2e9';
const SURFACE_SUBTLE = '#fafafb';

// Chart colors.
const C_PRECISION = '#1777ff';
const C_RECALL = '#eb2f96';
const C_FBETA = '#722ed1';
const C_ACCURACY = '#52c41a';
const C_UNCAL = '#d3adf7';
// Orange marks the user-dragged ("current") threshold; light blue marks the optimal one.
const C_BEST = '#fa8c16';
const C_OPTIMAL = '#69b1ff';
// Precision-Recall area fills: purple below the diagonal, blue between diagonal and curve.
const C_DIAGONAL = '#9254de';
const C_BAND_BELOW = '#b37feb';
const C_BAND_ABOVE = '#1777ff';

// Delta colors.
const DELTA_UP = '#389e0d';
const DELTA_DOWN = '#cf1322';

// Render the slider step marks as thin vertical ticks sitting below the 4px
// rail (antd default) instead of the default circular dots. `top` adds the gap
// between the rail and the ticks.
const SLIDER_TICK_STYLE = {
    width: 1,
    height: 6,
    top: 10,
    border: 'none',
    borderRadius: 0,
    backgroundColor: BORDER,
    cursor: 'default',
};

// Extra vertical gap pushing the labels further below the ticks.
const SLIDER_LABEL_TOP = 12;
const SLIDER_LABEL_LINE_HEIGHT = 14;

// Vertical rhythm of the Threshold card. Kept tight so the card (Threshold +
// False Positive Rate sections) fits the natural height of the Quality
// Metrics card beside it instead of stretching it.
const SECTION_TITLE_GAP = 4;
const DESCRIPTION_LINE_HEIGHT = '20px';
const SLIDER_ROW_GAP = 8;
const SECTION_DIVIDER_GAP = 6;
// antd's with-marks slider reserves 11px above and 30px below its 12px control
// box. The marks strip starts at the box's bottom edge, so the labels end
// SLIDER_LABEL_TOP + one line below it; reserve exactly that.
const SLIDER_STYLE = {
    marginTop: 4,
    marginBottom: SLIDER_LABEL_TOP + SLIDER_LABEL_LINE_HEIGHT,
};

const f3 = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(3) : '—');
const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

// Slider marks: a thin tick every 0.05 plus three labels. Edge labels are
// centered on the rail by default, which pushes the leftmost/rightmost label
// outside the card, so pin them to the rail edge; every label is pushed down so
// it clears the ticks.
const buildSliderMarks = (startLabel, midLabel, endLabel) => {
    const labelStyle = {
        fontSize: 13,
        lineHeight: `${SLIDER_LABEL_LINE_HEIGHT}px`,
        color: TEXT_SECONDARY,
        whiteSpace: 'nowrap',
    };
    // antd's mark-text box keeps its own 22px line box; pin it to the label
    // line so the slider's bottom margin math above holds.
    const markStyle = { top: SLIDER_LABEL_TOP, lineHeight: `${SLIDER_LABEL_LINE_HEIGHT}px` };
    const marks = {};
    for (let i = 0; i <= 20; i += 1) marks[i / 20] = '';
    marks[0] = {
        label: <span style={labelStyle}>{startLabel}</span>,
        style: { ...markStyle, transform: 'translateX(0)' },
    };
    marks[0.5] = {
        label: <span style={labelStyle}>{midLabel}</span>,
        style: markStyle,
    };
    marks[1] = {
        label: <span style={labelStyle}>{endLabel}</span>,
        style: { ...markStyle, transform: 'translateX(-100%)' },
    };
    return marks;
};

const THRESHOLD_SLIDER_MARKS = buildSliderMarks('Eager (0.0)', 'Balanced (0.5)', 'Cautious (1.0)');
const FPR_SLIDER_MARKS = buildSliderMarks('0.0', '0.5', '1.0');

// Bordered value box sitting to the right of each slider.
const VALUE_BOX_LINE_HEIGHT = 22;
const VALUE_BOX_PADDING_Y = 6;
// Fixed height: while loading a 16px spinner stands in for the 22px text
// line, and without this the box would shrink and grow on every preview.
const VALUE_BOX_HEIGHT = VALUE_BOX_LINE_HEIGHT + 2 * VALUE_BOX_PADDING_Y + 2;
const valueBoxStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: `${VALUE_BOX_PADDING_Y}px 4px`,
    height: VALUE_BOX_HEIGHT,
    color: TEXT_PRIMARY,
    fontSize: 14,
    lineHeight: `${VALUE_BOX_LINE_HEIGHT}px`,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    flex: 'none',
};

// Quality Metrics cells. The delta is wrapped by TooltipTrigger (inline-flex,
// vertical-align: middle), which stretches an inline line box by a pixel or
// two, so rows with a delta were taller than rows showing the loading spinner
// or nothing. The Threshold card beside the table stretches to the table's
// height with its False Positive Rate section pinned to the bottom, so that
// difference moved the FPR section and every card below it on each preview.
// A fixed-height flex cell makes the row height independent of its content.
const QUALITY_CELL_HEIGHT = 22;
const qualityCellStyle = {
    display: 'flex',
    alignItems: 'center',
    height: QUALITY_CELL_HEIGHT,
    whiteSpace: 'nowrap',
};

/**
 * Editable numeric box for a [0, 1] value. Only digits and a single decimal
 * point can be typed; the value is committed on Enter or blur (Escape reverts)
 * and the caller clamps it to [0, 1]. While ``loading`` the number is replaced
 * by a spinner so a stale value is never shown.
 */
const ValueInput = ({ value, onCommit, loading = false, width = 60, adornment = null, ariaLabel }) => {
    const [text, setText] = useState(null); // null => not editing
    const skipCommitRef = useRef(false);
    const display = value == null ? '—' : round3(value).toFixed(3);

    // The input is swapped for a spinner while loading; drop any half-typed
    // text so it does not reappear once the value is confirmed.
    useEffect(() => {
        if (loading) setText(null);
    }, [loading]);

    const commit = (evt) => {
        if (skipCommitRef.current) {
            skipCommitRef.current = false;
            return;
        }
        if (text === null) return;
        setText(null);
        onCommit(evt.target.value);
    };

    return (
        <div style={{ ...valueBoxStyle, width }}>
            {loading ? (
                <Spinner size="small" aria-label={`Updating ${ariaLabel}`} />
            ) : (
                <input
                    type="text"
                    inputMode="decimal"
                    aria-label={ariaLabel}
                    value={text === null ? display : text}
                    onFocus={() => setText(value == null ? '' : round3(value).toFixed(3))}
                    onChange={(evt) => setText(sanitizeDecimalInput(evt.target.value))}
                    onBlur={commit}
                    onKeyDown={(evt) => {
                        if (evt.key === 'Enter') {
                            evt.currentTarget.blur();
                        } else if (evt.key === 'Escape') {
                            skipCommitRef.current = true;
                            setText(null);
                            evt.currentTarget.blur();
                        }
                    }}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        padding: 0,
                        textAlign: 'center',
                        font: 'inherit',
                        color: 'inherit',
                    }}
                />
            )}
            {!loading && adornment}
        </div>
    );
};

// Pan offset (in data units) of the zoomed window's center from 0.5, clamped so
// the visible window stays inside [0, 1] for the given zoom level.
const clampPan = (offset, zoom) => {
    const limit = 0.5 - 0.5 / zoom;
    return Math.min(limit, Math.max(-limit, offset));
};

// Visible axis [min, max] for a zoom level and (clamped) pan offset.
const zoomDomain = (zoom, panOffset) => {
    const half = 0.5 / zoom;
    const center = 0.5 + clampPan(panOffset, zoom);
    return [round3(center - half), round3(center + half)];
};

// Some metric sources are plain scalars (compute-metrics-for-thresholds), others
// are { oldModel, newModel } comparison objects (the stored evaluation metrics).
const num = (v) => {
    if (typeof v === 'number') return v;
    if (v && typeof v === 'object') return v.newModel;
    return undefined;
};

const formatBetaShort = (b) => {
    if (b === null || b === undefined) return '1';
    const n = Number(b);
    if (Number.isNaN(n)) return '1';
    const r = Math.round(n * 10) / 10;
    return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

const cardStyle = {
    backgroundColor: 'white',
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: 20,
};

// Subtle top-to-bottom gradient "background image" sampled from the Figma design
// (white fading into a light gray).
const STAT_CARD_BG = '#f6f7f9';

// Shared style for the Current / Optimal threshold stat cards so both cards are
// visually identical (same background image, border, radius, padding).
// Padding (16px horizontal / 12px vertical) and the 12px gap between the value
// and the sub line match the Figma frame "Frame 1000003090" (spacing/16 + spacing/12).
const statCardStyle = {
    background: STAT_CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: '12px 16px',
};

const statCardLabelStyle = { fontSize: 12, color: TEXT_SECONDARY, marginBottom: 8 };
const statCardValueStyle = { fontSize: 22, fontWeight: 600, color: TEXT_PRIMARY };
const statCardSubStyle = { fontSize: 12, color: TEXT_SECONDARY, marginTop: 12 };

// Selected-tab accent for the chart toggle (Figma node 5325-9288).
const TAB_SELECTED = '#008ef5';

const CHART_TABS = [
    { label: 'PR Curve', value: 'pr' },
    { label: 'Metrics vs Threshold', value: 'metrics' },
];

// Chart view toggle, styled to match Figma (two joined buttons on white: selected
// = blue border + blue medium text, unselected = gray border + dark text). Replaces
// antd <Segmented> whose animated thumb could drop the selected indicator after a
// click; here the selected state is driven directly by `value` so it can't vanish.
const ChartModeToggle = ({ value, onChange }) => (
    <div style={{ display: 'inline-flex' }}>
        {CHART_TABS.map((tab, i) => {
            const selected = value === tab.value;
            const first = i === 0;
            const last = i === CHART_TABS.length - 1;
            return (
                <button
                    key={tab.value}
                    type="button"
                    onClick={() => onChange(tab.value)}
                    style={{
                        position: 'relative',
                        zIndex: selected ? 1 : 0,
                        height: 24,
                        padding: '0 8px',
                        marginLeft: first ? 0 : -1,
                        background: '#fff',
                        color: selected ? TAB_SELECTED : TEXT_PRIMARY,
                        fontWeight: selected ? 500 : 400,
                        fontSize: 14,
                        lineHeight: '22px',
                        fontFamily: 'inherit',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'none',
                        border: `1px solid ${selected ? TAB_SELECTED : BORDER}`,
                        borderTopLeftRadius: first ? 4 : 0,
                        borderBottomLeftRadius: first ? 4 : 0,
                        borderTopRightRadius: last ? 4 : 0,
                        borderBottomRightRadius: last ? 4 : 0,
                    }}
                >
                    {tab.label}
                </button>
            );
        })}
    </div>
);

/**
 * Redesigned confidence-threshold panel for the Evaluation screen.
 *
 * Rendered only when a specific label/class is selected (gated by the caller).
 * Lays out: a Threshold card + Quality Metrics card (top row), a
 * Precision-Recall Trade-off card (PR Curve / Metrics vs Threshold with a
 * draggable threshold line), and Current / Optimal threshold cards.
 *
 * Reuses the existing live plumbing: ``onLiveThresholdsChange`` drives the
 * /compute-metrics-for-thresholds preview and the Samples override; Save All
 * persists via /update-selected-threshold.
 */
const ThresholdPanel = ({
    llmUsecaseId,
    version,
    calibrationTemperature,
    selectedThresholdConfig,
    selectedThresholds,
    thresholdCandidates,
    baselineThresholds,
    beta,
    allMetrics,
    customThresholdMetrics,
    customThresholdLoading = false,
    onThresholdSaved,
    onLiveThresholdsChange,
    selectedLabel,
    thresholdOnly = false,
    // NER entity thresholds have TN = 0 by convention, so FPR is undefined there
    // and the caller hides the section.
    showFalsePositiveRate = true,
}) => {
    const calibrated = thresholdCandidates?.calibrated || null;
    const uncalibrated = thresholdCandidates?.uncalibrated || null;

    const labelData = calibrated?.[selectedLabel] || null;

    const candidates = useMemo(() => {
        const cands = labelData?.candidates || [];
        return [...cands].sort((a, b) => a.threshold - b.threshold);
    }, [labelData]);

    const uncalCandidates = useMemo(() => {
        const cands = uncalibrated?.[selectedLabel]?.candidates || [];
        return [...cands].sort((a, b) => a.threshold - b.threshold);
    }, [uncalibrated, selectedLabel]);

    const optimalThreshold = useMemo(() => {
        if (labelData && typeof labelData.optimal_threshold === 'number') {
            return labelData.optimal_threshold;
        }
        return DEFAULT_THRESHOLD;
    }, [labelData]);

    const baselineValue = useMemo(() => {
        if (
            selectedThresholdConfig === 'custom' &&
            selectedThresholds &&
            selectedThresholds[selectedLabel] != null
        ) {
            return selectedThresholds[selectedLabel];
        }
        if (baselineThresholds && baselineThresholds[selectedLabel] != null) {
            return baselineThresholds[selectedLabel];
        }
        return optimalThreshold;
    }, [
        selectedThresholdConfig,
        selectedThresholds,
        baselineThresholds,
        selectedLabel,
        optimalThreshold,
    ]);

    const [value, setValue] = useState(baselineValue);
    const [chartMode, setChartMode] = useState('pr');
    const [saving, setSaving] = useState(false);
    const [showSavedNotice, setShowSavedNotice] = useState(false);
    const draggingRef = useRef(false);
    // FPR target the user dragged/typed. FPR is step-wise in the threshold, so
    // the threshold it maps to usually has a slightly lower (or the same) real
    // FPR; the target stays in the box and on the handle so the control does
    // not snap away from the user's hand. It is dropped only when the threshold
    // is moved by another control (slider, box, chart, Optimize, Reset) or the
    // label changes, which is when the real FPR becomes the relevant number.
    const [fprDraft, setFprDraft] = useState(null);
    // Latest threshold, readable from the baseline effect below without
    // re-running it on every drag.
    const valueRef = useRef(value);
    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    // Chart zoom: 1 = full [0, 1] view; higher zooms in by narrowing both axis
    // domains. `pan` offsets the visible window's center so the user can drag to
    // reveal clipped areas once zoomed in. The +/- buttons step zoom
    // multiplicatively and recenter the view.
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const chartWrapperRef = useRef(null);
    const panningRef = useRef(false);
    const lastPanPosRef = useRef(null);
    // Mirror zoom/pan into refs so the native (non-passive) wheel listener can read
    // the latest values without being re-attached on every change.
    const zoomRef = useRef(zoom);
    const panRef = useRef(pan);
    useEffect(() => {
        zoomRef.current = zoom;
    }, [zoom]);
    useEffect(() => {
        panRef.current = pan;
    }, [pan]);

    const zoomIn = useCallback(() => {
        setZoom((z) => Math.min(ZOOM_MAX, round3(z * ZOOM_FACTOR)));
        setPan({ x: 0, y: 0 });
    }, []);
    const zoomOut = useCallback(() => {
        setZoom((z) => Math.max(ZOOM_MIN, round3(z / ZOOM_FACTOR)));
        setPan({ x: 0, y: 0 });
    }, []);

    const xDomain = useMemo(() => zoomDomain(zoom, pan.x), [zoom, pan.x]);
    const yDomain = useMemo(() => zoomDomain(zoom, pan.y), [zoom, pan.y]);

    // Drag-to-pan (only while zoomed in). Converts mouse pixel deltas into data
    // units using the rendered plot size; grabbing and moving the chart shifts the
    // visible window the opposite way (natural "grab" panning).
    const onPanDown = useCallback(
        (evt) => {
            if (zoom <= 1) return;
            panningRef.current = true;
            lastPanPosRef.current = { x: evt.clientX, y: evt.clientY };
            evt.preventDefault();
        },
        [zoom]
    );
    const onPanMove = useCallback(
        (evt) => {
            if (!panningRef.current || zoom <= 1) return;
            const last = lastPanPosRef.current;
            if (!last) return;
            const dxPx = evt.clientX - last.x;
            const dyPx = evt.clientY - last.y;
            lastPanPosRef.current = { x: evt.clientX, y: evt.clientY };
            const el = chartWrapperRef.current;
            const w = el ? el.clientWidth : 600;
            const h = el ? el.clientHeight : 300;
            // Approximate plot area (excludes axes/margins) so panning speed
            // tracks the cursor reasonably across the visible data span.
            const plotW = Math.max(1, w - 92);
            const plotH = Math.max(1, h - 66);
            const span = 1 / zoom;
            const dDataX = (dxPx / plotW) * span;
            const dDataY = (dyPx / plotH) * span;
            setPan((p) => ({
                x: clampPan(p.x - dDataX, zoom),
                y: clampPan(p.y + dDataY, zoom),
            }));
        },
        [zoom]
    );
    const stopPan = useCallback(() => {
        panningRef.current = false;
        lastPanPosRef.current = null;
    }, []);

    // Scroll-to-zoom, anchored to the cursor, but only while the pointer is over
    // the chart. Uses a native non-passive listener so we can preventDefault and
    // stop the page from scrolling.
    useEffect(() => {
        const el = chartWrapperRef.current;
        if (!el) return undefined;
        const onWheel = (e) => {
            e.preventDefault();
            const z = zoomRef.current;
            const p = panRef.current;
            const newZoom =
                e.deltaY < 0
                    ? Math.min(ZOOM_MAX, round3(z * ZOOM_FACTOR))
                    : Math.max(ZOOM_MIN, round3(z / ZOOM_FACTOR));
            if (newZoom === z) return;
            // Keep the data point under the cursor fixed across the zoom change.
            const [x0, x1] = zoomDomain(z, p.x);
            const [y0, y1] = zoomDomain(z, p.y);
            const rect = el.getBoundingClientRect();
            const plotW = Math.max(1, rect.width - 92);
            const plotH = Math.max(1, rect.height - 66);
            const fx = Math.min(1, Math.max(0, (e.clientX - rect.left - 68) / plotW));
            const fy = Math.min(1, Math.max(0, (e.clientY - rect.top - 12) / plotH));
            const dataX = x0 + fx * (x1 - x0);
            const dataY = y1 - fy * (y1 - y0);
            const half = 0.5 / newZoom;
            setZoom(newZoom);
            setPan({
                x: clampPan(dataX - half * (2 * fx - 1) - 0.5, newZoom),
                y: clampPan(dataY - half * (1 - 2 * fy) - 0.5, newZoom),
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
        // Re-bind once the chart actually mounts (the component early-returns null
        // until calibration/label data is available, so the ref starts out null).
    }, [thresholdOnly, calibrationTemperature, labelData, candidates.length]);

    // A new baseline (saved thresholds refetched, parent-driven change) resets
    // the threshold. The FPR target survives only when that leaves the
    // threshold where it was (e.g. right after Save), since the user's number
    // still describes the shown threshold then.
    useEffect(() => {
        if (Math.abs(valueRef.current - baselineValue) > 1e-9) setFprDraft(null);
        setValue(baselineValue);
    }, [baselineValue]);

    // Each label has its own FPR; a target typed for one label means nothing
    // for the next.
    useEffect(() => {
        setFprDraft(null);
    }, [selectedLabel]);

    // Merge the dragged label's threshold over the full baseline map so that the
    // "Impact Overall" preview and the Samples override keep every other label at
    // its saved/optimal threshold (instead of defaulting to 0.0 server-side).
    const emit = useCallback(
        (next) => {
            if (typeof onLiveThresholdsChange !== 'function') return;
            onLiveThresholdsChange({
                ...(baselineThresholds || {}),
                [selectedLabel]: next,
            });
        },
        [onLiveThresholdsChange, baselineThresholds, selectedLabel]
    );

    const setThreshold = useCallback(
        (next) => {
            setValue(next);
            emit(next);
        },
        [emit]
    );

    // Threshold moved by a threshold control (slider, box, chart, Optimize,
    // Reset): the real FPR of the new threshold replaces any FPR target.
    const apply = useCallback(
        (next) => {
            setFprDraft(null);
            setThreshold(next);
        },
        [setThreshold]
    );

    const handleOptimize = useCallback(() => {
        apply(optimalThreshold);
    }, [apply, optimalThreshold]);

    const handleReset = useCallback(() => {
        apply(baselineValue);
    }, [apply, baselineValue]);

    const handleSaveAll = useCallback(() => {
        setSaving(true);
        const fullMap = { ...(baselineThresholds || {}), [selectedLabel]: value };
        const isOptimal = Object.entries(fullMap).every(
            ([lbl, v]) =>
                Math.abs(v - (calibrated?.[lbl]?.optimal_threshold ?? v)) < 1e-6
        );
        data_from_server_post(
            'update-selected-threshold',
            {
                llm_usecase_id: llmUsecaseId,
                version,
                threshold_config: isOptimal ? 'optimal_calibrated' : 'custom',
                custom_thresholds: isOptimal ? null : fullMap,
            },
            (response) => {
                setSaving(false);
                if (response && response.success === false) {
                    sparkToast.error(response.error || 'Failed to save thresholds');
                    return;
                }
                setShowSavedNotice(true);
                sparkToast.success(`Threshold saved for label ${selectedLabel}`);
                if (typeof onThresholdSaved === 'function') onThresholdSaved();
            },
            () => {
                setSaving(false);
                sparkToast.error('Failed to save thresholds');
            }
        );
    }, [
        baselineThresholds,
        selectedLabel,
        value,
        calibrated,
        llmUsecaseId,
        version,
        onThresholdSaved,
    ]);

    const nearestByThreshold = useCallback(
        (t) => {
            if (!candidates.length) return null;
            return candidates.reduce(
                (best, c) =>
                    Math.abs(c.threshold - t) < Math.abs(best.threshold - t) ? c : best,
                candidates[0]
            );
        },
        [candidates]
    );

    const nearestByRecall = useCallback(
        (r) => {
            if (!candidates.length) return null;
            return candidates.reduce(
                (best, c) =>
                    Math.abs(c.recall - r) < Math.abs(best.recall - r) ? c : best,
                candidates[0]
            );
        },
        [candidates]
    );

    const currentCand = useMemo(
        () => nearestByThreshold(value),
        [nearestByThreshold, value]
    );

    // Map a target FPR to a threshold (lowest threshold whose FPR stays within
    // the target) and move there. On an FPR plateau the mapped threshold is the
    // current one; it is then left untouched (no preview round-trip), and in
    // both cases the target itself is what the FPR controls keep showing.
    const applyFpr = useCallback(
        (targetFpr) => {
            const next = thresholdForFpr(candidates, targetFpr);
            if (next == null) return;
            if (Math.abs(next - value) > 1e-9) setThreshold(next);
            setFprDraft(targetFpr);
        },
        [candidates, value, setThreshold]
    );

    // FPR = FP / (FP + TN) for the selected label. Prefer the live preview
    // matrix (/compute-metrics-for-thresholds, the same source as the Confusion
    // Matrix card); at the saved threshold use the stored matrix; otherwise fall
    // back to the nearest PR-curve candidate's matrix as an interim estimate.
    const displayedFpr = useMemo(() => {
        if (customThresholdMetrics) {
            const live = falsePositiveRate(
                customThresholdMetrics.eval_class_metrics?.[selectedLabel]?.confusion_matrix
            );
            if (live != null) return live;
        }
        if (Math.abs(value - baselineValue) < 1e-6) {
            const stored = falsePositiveRate(
                allMetrics?.eval_class_metrics?.[selectedLabel]?.confusion_matrix
            );
            if (stored != null) return stored;
        }
        return falsePositiveRate(currentCand?.confusion_matrix);
    }, [customThresholdMetrics, allMetrics, selectedLabel, value, baselineValue, currentCand]);

    // What the FPR slider and box show: the user's target while one is set,
    // otherwise the real FPR of the current threshold.
    const shownFpr = fprDraft ?? displayedFpr;

    // Chart datasets.
    const metricsData = useMemo(
        () =>
            candidates.map((c) => ({
                threshold: round3(c.threshold),
                precision: round3(c.precision),
                recall: round3(c.recall),
                f_beta_score: round3(c.f_beta_score),
                accuracy: round3(c.accuracy),
            })),
        [candidates]
    );

    const prCalData = useMemo(
        () =>
            candidates
                .map((c) => {
                    const recall = round3(c.recall);
                    const precision = round3(c.precision);
                    // Diagonal baseline from (0,1) to (1,0): precision = 1 - recall.
                    const diagonal = round3(1 - recall);
                    // Blue band fills only the region above the diagonal up to the curve.
                    const band =
                        precision > diagonal ? [diagonal, precision] : [diagonal, diagonal];
                    return { recall, precision, diagonal, band };
                })
                .sort((a, b) => a.recall - b.recall),
        [candidates]
    );

    const prUncalData = useMemo(
        () =>
            uncalCandidates
                .map((c) => ({ recall: round3(c.recall), precision: round3(c.precision) }))
                .sort((a, b) => a.recall - b.recall),
        [uncalCandidates]
    );

    const recallAtCurrent = currentCand ? round3(currentCand.recall) : null;
    const precisionAtCurrent = currentCand ? round3(currentCand.precision) : null;

    const optimalCand = useMemo(
        () => nearestByThreshold(optimalThreshold),
        [nearestByThreshold, optimalThreshold]
    );
    const recallAtOptimal = optimalCand ? round3(optimalCand.recall) : null;
    const precisionAtOptimal = optimalCand ? round3(optimalCand.precision) : null;

    const moveFromEvent = useCallback(
        (e) => {
            if (!e) return;
            const x = e.activeLabel;
            if (x == null) return;
            const cand =
                chartMode === 'pr' ? nearestByRecall(x) : nearestByThreshold(x);
            if (cand) apply(cand.threshold);
        },
        [chartMode, nearestByRecall, nearestByThreshold, apply]
    );

    const onChartDown = useCallback(
        (e) => {
            // While zoomed in, dragging pans the view instead of setting threshold.
            if (zoom > 1) return;
            draggingRef.current = true;
            moveFromEvent(e);
        },
        [zoom, moveFromEvent]
    );
    const onChartMove = useCallback(
        (e) => {
            if (zoom > 1) return;
            if (draggingRef.current) moveFromEvent(e);
        },
        [zoom, moveFromEvent]
    );
    const stopDrag = useCallback(() => {
        draggingRef.current = false;
    }, []);

    if (calibrationTemperature == null) return null;
    if (!labelData || !candidates.length) return null;

    const betaShort = formatBetaShort(beta);
    const fBetaLabel = `F${betaShort}`;
    const hasChanges = Math.abs(value - baselineValue) > 1e-6;
    const isAtOptimal = Math.abs(value - optimalThreshold) < 1e-6;
    // Warn when the threshold is pushed to either extreme of the slider.
    const edgeWarning =
        value >= HIGH_THRESHOLD_EDGE
            ? 'A high threshold prioritizes precision, returning fewer predictions and increasing the risk of missing true cases.'
            : value <= LOW_THRESHOLD_EDGE
                ? 'A low threshold prioritizes recall, returning more predictions and increasing the risk of false positives.'
                : null;
    const versionLabel = `This Label`;

    const metricRows = [
        { key: 'accuracy', label: 'Accuracy' },
        { key: 'precision', label: 'Precision' },
        { key: 'recall', label: 'Recall' },
        { key: 'f_beta_score', label: `${fBetaLabel} score` },
    ];

    const labelMetricsSource = customThresholdMetrics
        ? customThresholdMetrics.eval_class_metrics?.[selectedLabel]
        : allMetrics?.eval_class_metrics?.[selectedLabel];
    const labelBaselineSource = allMetrics?.eval_class_metrics?.[selectedLabel];
    const overallCurrentSource = customThresholdMetrics
        ? customThresholdMetrics.eval_general_metrics
        : allMetrics?.eval_general_metrics;
    const overallBaselineSource = allMetrics?.eval_general_metrics;

    // Green/red delta between the live (custom-threshold) preview value and the
    // stored baseline; null when no preview is active or the change is below
    // display precision.
    const deltaBetween = (currentVal, baseVal) => {
        if (!customThresholdMetrics) return null;
        if (!Number.isFinite(currentVal) || !Number.isFinite(baseVal)) return null;
        const d = currentVal - baseVal;
        return Math.abs(d) >= 5e-4 ? d : null;
    };

    // Tooltip explaining what a red/green delta represents, e.g.
    // “Precision across all labels improves by 0.012 at the selected
    // threshold, compared to the saved threshold.”
    const deltaTooltip = (delta, metricLabel, scope) => {
        if (delta == null) return '';
        const verb = delta >= 0 ? 'improves' : 'drops';
        return `${metricLabel} ${scope} ${verb} by ${Math.abs(delta).toFixed(
            3
        )} at the current selected threshold, compared to the saved threshold.`;
    };

    // While the threshold-preview request is pending, a spinner takes the place
    // of the delta so stale red/green numbers are never shown. Both the spinner
    // and the delta sit in a fixed-height cell (qualityCellStyle), so the swap
    // never changes the row height.
    const renderDelta = (delta, tooltip) => {
        if (customThresholdLoading) {
            return (
                <span style={{ marginLeft: 8, display: 'inline-flex' }}>
                    <Spinner size="small" aria-label="Updating metrics" />
                </span>
            );
        }
        if (delta == null) return null;
        return (
            <TooltipTrigger text={tooltip}>
                <span
                    style={{
                        marginLeft: 8,
                        fontWeight: 600,
                        color: delta >= 0 ? DELTA_UP : DELTA_DOWN,
                        cursor: 'help',
                    }}
                >
                    {delta >= 0 ? '+' : ''}
                    {delta.toFixed(3)}
                </span>
            </TooltipTrigger>
        );
    };

    const renderQualityRow = ({ key, label }) => {
        const labelVal = num(labelMetricsSource?.[key]);
        const labelBase = num(labelBaselineSource?.[key]);
        const overallVal = num(overallCurrentSource?.[key]);
        const overallBase = num(overallBaselineSource?.[key]);
        const labelDelta = deltaBetween(labelVal, labelBase);
        const overallDelta = deltaBetween(overallVal, overallBase);
        return (
            <div
                key={key}
                style={{
                    display: 'grid',
                    gridTemplateColumns: '0.8fr 1fr 1fr',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderTop: `1px solid ${BORDER}`,
                    fontSize: 14,
                    color: TEXT_PRIMARY,
                }}
            >
                <span style={qualityCellStyle}>{label}</span>
                <span style={qualityCellStyle} data-testid={`quality-${key}-label`}>
                    {Number.isFinite(labelVal) ? f3(labelVal) : '—'}
                    {renderDelta(
                        labelDelta,
                        deltaTooltip(labelDelta, label, `for “${selectedLabel}”`)
                    )}
                </span>
                <span style={qualityCellStyle} data-testid={`quality-${key}-overall`}>
                    {Number.isFinite(overallVal) ? f3(overallVal) : '—'}
                    {renderDelta(
                        overallDelta,
                        deltaTooltip(overallDelta, label, 'across all labels')
                    )}
                </span>
            </div>
        );
    };

    const MetricsTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
        return (
            <div
                style={{
                    background: '#1f2430',
                    color: 'white',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                }}
            >
                <div style={{ marginBottom: 4 }}>{Number(label).toFixed(3)}</div>
                {payload.map((p) => (
                    <div
                        key={p.dataKey}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: p.color,
                                display: 'inline-block',
                            }}
                        />
                        {p.name}: {Number(p.value).toFixed(3)}
                    </div>
                ))}
            </div>
        );
    };

    const PrTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;
        const precisionEntry = payload.find((p) => p.dataKey === 'precision');
        if (!precisionEntry) return null;
        const { recall } = precisionEntry.payload || {};
        return (
            <div
                style={{
                    background: '#1f2430',
                    color: 'white',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                }}
            >
                <div>Recall: {Number(recall).toFixed(3)}</div>
                <div>Precision: {Number(precisionEntry.value).toFixed(3)}</div>
            </div>
        );
    };

    const legendItem = (color, text) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: color,
                    display: 'inline-block',
                }}
            />
            <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>{text}</span>
        </span>
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                marginBottom: 16,
            }}
        >
            {/* Top row: Threshold + Quality Metrics. Quality Metrics keeps its
                natural height (flex-start); the Threshold card stretches to
                match it, so the two line up without Quality Metrics growing.
                Any spare height opens up above the False Positive Rate divider
                (its wrapper has `marginTop: auto`). */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                {/* Threshold card */}
                <div
                    style={{
                        ...cardStyle,
                        flex: 1,
                        minWidth: 0,
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: SECTION_TITLE_GAP,
                        }}
                    >
                        <span style={{ fontSize: 16, fontWeight: 600, color: TEXT_PRIMARY }}>
                            Threshold
                        </span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {hasChanges && (
                                <Button
                                    variant="ghost"
                                    size="medium"
                                    showStartIcon
                                    startIcon={<SparkIcon slug="refresh" size="medium" />}
                                    onClick={handleReset}
                                >
                                    Reset
                                </Button>
                            )}
                            <Button variant="secondary" size="medium" showStartIcon startIcon={<BoltIcon />} onClick={handleOptimize}>
                                {`Optimize ${fBetaLabel}`}
                            </Button>
                            <Button variant="primary" size="medium" disabled={saving} disableWhenReadOnly onClick={handleSaveAll}>
                                {saving ? 'Saving…' : 'Save'}
                            </Button>
                        </div>
                    </div>
                    <div style={{ color: TEXT_SECONDARY, fontSize: 14, lineHeight: DESCRIPTION_LINE_HEIGHT }}>
                        Lower thresholds catch more cases, while higher thresholds reduce
                        false positives.
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            marginTop: SLIDER_ROW_GAP,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <Slider
                                style={SLIDER_STYLE}
                                min={0}
                                max={1}
                                step={0.01}
                                value={value}
                                onChange={apply}
                                marks={THRESHOLD_SLIDER_MARKS}
                                dotStyle={SLIDER_TICK_STYLE}
                                activeDotStyle={SLIDER_TICK_STYLE}
                                tooltip={{ formatter: (v) => v?.toFixed?.(2) }}
                            />
                        </div>
                        <ValueInput
                            ariaLabel="Threshold"
                            value={value}
                            width={isAtOptimal ? 74 : 60}
                            onCommit={(text) => {
                                const next = parseUnitInterval(text, value);
                                if (Math.abs(next - value) > 1e-9) apply(next);
                            }}
                            adornment={
                                isAtOptimal ? (
                                    <TooltipTrigger text={`Optimal ${fBetaLabel} threshold`}>
                                        <span><CrownIcon style={{ verticalAlign: 'middle' }} /></span>
                                    </TooltipTrigger>
                                ) : null
                            }
                        />
                    </div>
                    {edgeWarning && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginTop: 16,
                                padding: '12px 16px',
                                border: `1px solid ${BORDER}`,
                                borderRadius: 8,
                            }}
                        >
                            <SparkIcon slug="circle-alert" size="medium" style={{ color: '#faad14' }} />
                            <div style={{ fontSize: 14, color: TEXT_PRIMARY }}>
                                {edgeWarning}
                            </div>
                        </div>
                    )}
                    {showSavedNotice && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginTop: 16,
                                padding: '12px 16px',
                                border: `1px solid ${BORDER}`,
                                borderRadius: 8,
                            }}
                        >
                            <SparkIcon slug="circle-alert" size="medium" style={{ color: '#faad14' }} />
                            <div style={{ fontSize: 14, color: TEXT_PRIMARY }}>
                                Deployed models require re-deployment to use updated
                                thresholds.
                            </div>
                        </div>
                    )}
                    {showFalsePositiveRate && (
                        <div style={{ marginTop: 'auto' }}>
                            {/* Same 1px line the Quality Metrics rows use as a separator. */}
                            <div
                                style={{
                                    borderTop: `1px solid ${BORDER}`,
                                    margin: `${SECTION_DIVIDER_GAP}px 0`,
                                }}
                            />
                            <div
                                style={{
                                    fontSize: 16,
                                    fontWeight: 600,
                                    color: TEXT_PRIMARY,
                                    marginBottom: SECTION_TITLE_GAP,
                                }}
                            >
                                False Positive Rate
                            </div>
                            <div
                                style={{
                                    color: TEXT_SECONDARY,
                                    fontSize: 14,
                                    lineHeight: DESCRIPTION_LINE_HEIGHT,
                                }}
                            >
                                How often negative samples are incorrectly flagged as this
                                label. Lower FPR means fewer false alarms, but may miss true
                                cases.
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 16,
                                    marginTop: SLIDER_ROW_GAP,
                                }}
                            >
                                <div style={{ flex: 1 }}>
                                    <Slider
                                        style={SLIDER_STYLE}
                                        min={0}
                                        max={1}
                                        step={0.01}
                                        disabled={displayedFpr == null}
                                        value={shownFpr ?? 0}
                                        onChange={applyFpr}
                                        marks={FPR_SLIDER_MARKS}
                                        dotStyle={SLIDER_TICK_STYLE}
                                        activeDotStyle={SLIDER_TICK_STYLE}
                                        tooltip={{ formatter: (v) => v?.toFixed?.(2) }}
                                    />
                                </div>
                                <ValueInput
                                    ariaLabel="False positive rate"
                                    value={shownFpr}
                                    loading={customThresholdLoading}
                                    onCommit={(text) => applyFpr(parseUnitInterval(text, shownFpr ?? 0))}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Quality Metrics card */}
                {!thresholdOnly && (
                    <div style={{ ...cardStyle, flex: 1, minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: 16,
                                fontWeight: 600,
                                color: TEXT_PRIMARY,
                                marginBottom: 12,
                            }}
                        >
                            Quality Metrics
                        </div>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '0.8fr 1fr 1fr',
                                padding: '10px 0',
                                background: SURFACE_SUBTLE,
                                fontSize: 12,
                                fontWeight: 600,
                                color: TEXT_SECONDARY,
                                letterSpacing: 0.4,
                            }}
                        >
                            <span />
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {versionLabel}
                                <TooltipTrigger text={`Metrics for the currently selected label (“${selectedLabel}”) using the current threshold.`}>
                                    <span style={{ cursor: 'help' }}>
                                        <SparkIcon slug="circle-help" size="small" style={{ color: TEXT_SECONDARY }} />
                                    </span>
                                </TooltipTrigger>
                            </span>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                All labels
                                <TooltipTrigger
                                    text={`Displays how changing the threshold for the selected label (“${selectedLabel}”) influences overall model quality across all labels.`}
                                >
                                    <span style={{ cursor: 'help' }}>
                                        <SparkIcon slug="circle-help" size="small" style={{ color: TEXT_SECONDARY }} />
                                    </span>
                                </TooltipTrigger>
                            </span>
                        </div>
                        {metricRows.map(renderQualityRow)}
                    </div>
                )}
            </div>

            {!thresholdOnly && (
                <>
                    {/* Precision-Recall Trade-off card */}
                    <div style={cardStyle}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 12,
                            }}
                        >
                            <span style={{ fontSize: 16, fontWeight: 600, color: TEXT_PRIMARY }}>
                                Precision-Recall Trade-off
                            </span>
                            <ChartModeToggle value={chartMode} onChange={setChartMode} />
                        </div>
                        <div style={{ position: 'relative' }}>
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Button
                                    variant="tertiary"
                                    size="small"
                                    iconSlug="plus"
                                    tooltip="Zoom in"
                                    aria-label="Zoom in"
                                    onClick={zoomIn}
                                    disabled={zoom >= ZOOM_MAX}
                                />
                                <Button
                                    variant="tertiary"
                                    size="small"
                                    iconSlug="minus"
                                    tooltip="Zoom out"
                                    aria-label="Zoom out"
                                    onClick={zoomOut}
                                    disabled={zoom <= ZOOM_MIN}
                                    style={{ marginTop: -1 }}
                                />
                            </div>
                            <div
                                ref={chartWrapperRef}
                                onMouseDown={onPanDown}
                                onMouseMove={onPanMove}
                                onMouseUp={stopPan}
                                onMouseLeave={stopPan}
                                style={{
                                    cursor: zoom > 1 ? 'grab' : 'ew-resize',
                                    userSelect: 'none',
                                }}
                            >
                                <ResponsiveContainer width="100%" height={300}>
                                    {chartMode === 'pr' ? (
                                        <ComposedChart
                                            data={prCalData}
                                            margin={{ top: 12, right: 24, bottom: 24, left: 8 }}
                                            onMouseDown={onChartDown}
                                            onMouseMove={onChartMove}
                                            onMouseUp={stopDrag}
                                            onMouseLeave={stopDrag}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="recall"
                                                type="number"
                                                domain={xDomain}
                                                allowDataOverflow
                                                tickCount={5}
                                                tickFormatter={(v) => v.toFixed(2)}
                                                label={{ value: 'Recall', position: 'insideBottom', offset: -12 }}
                                            />
                                            <YAxis
                                                type="number"
                                                domain={yDomain}
                                                allowDataOverflow
                                                tickCount={5}
                                                tickFormatter={(v) => v.toFixed(2)}
                                                label={{ value: 'Precision', angle: -90, position: 'insideLeft' }}
                                            />
                                            <RechartsTooltip content={<PrTooltip />} />
                                            {/* Purple fill beneath the diagonal baseline. */}
                                            <Area
                                                dataKey="diagonal"
                                                name="Baseline"
                                                legendType="none"
                                                stroke="none"
                                                fill={C_BAND_BELOW}
                                                fillOpacity={0.22}
                                                dot={false}
                                                activeDot={false}
                                                isAnimationActive={false}
                                            />
                                            {/* Blue fill between the diagonal and the precision curve. */}
                                            <Area
                                                dataKey="band"
                                                name="Gain"
                                                legendType="none"
                                                stroke="none"
                                                fill={C_BAND_ABOVE}
                                                fillOpacity={0.18}
                                                dot={false}
                                                activeDot={false}
                                                isAnimationActive={false}
                                            />
                                            {prUncalData.length > 0 && (
                                                <Line
                                                    data={prUncalData}
                                                    dataKey="precision"
                                                    name="Uncalibrated"
                                                    stroke={C_UNCAL}
                                                    strokeDasharray="5 5"
                                                    dot={false}
                                                    isAnimationActive={false}
                                                />
                                            )}
                                            {/* Linear dotted diagonal from (0,1) to (1,0). */}
                                            <ReferenceLine
                                                segment={[
                                                    { x: 0, y: 1 },
                                                    { x: 1, y: 0 },
                                                ]}
                                                stroke={C_DIAGONAL}
                                                strokeDasharray="5 5"
                                                ifOverflow="hidden"
                                            />
                                            <Line
                                                dataKey="precision"
                                                name="Precision"
                                                stroke={C_PRECISION}
                                                strokeWidth={2}
                                                dot={false}
                                                isAnimationActive={false}
                                            />
                                            {recallAtOptimal != null && (
                                                <ReferenceLine
                                                    x={recallAtOptimal}
                                                    stroke={C_OPTIMAL}
                                                    strokeDasharray="4 4"
                                                    label={{
                                                        value: 'optimal',
                                                        position: 'insideBottom',
                                                        fill: C_OPTIMAL,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            )}
                                            {!isAtOptimal && recallAtCurrent != null && (
                                                <ReferenceLine
                                                    x={recallAtCurrent}
                                                    stroke={C_BEST}
                                                    strokeDasharray="4 4"
                                                />
                                            )}
                                            {recallAtCurrent != null && precisionAtCurrent != null && (
                                                <ReferenceDot
                                                    x={recallAtCurrent}
                                                    y={precisionAtCurrent}
                                                    r={5}
                                                    fill="white"
                                                    stroke={isAtOptimal ? C_OPTIMAL : C_BEST}
                                                    strokeWidth={2}
                                                />
                                            )}
                                        </ComposedChart>
                                    ) : (
                                        <LineChart
                                            data={metricsData}
                                            margin={{ top: 12, right: 24, bottom: 24, left: 8 }}
                                            onMouseDown={onChartDown}
                                            onMouseMove={onChartMove}
                                            onMouseUp={stopDrag}
                                            onMouseLeave={stopDrag}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="threshold"
                                                type="number"
                                                domain={xDomain}
                                                allowDataOverflow
                                                tickCount={5}
                                                tickFormatter={(v) => v.toFixed(2)}
                                                label={{ value: 'Threshold', position: 'insideBottom', offset: -12 }}
                                            />
                                            <YAxis
                                                type="number"
                                                domain={yDomain}
                                                allowDataOverflow
                                                tickCount={5}
                                                tickFormatter={(v) => v.toFixed(2)}
                                                label={{ value: 'Metric', angle: -90, position: 'insideLeft' }}
                                            />
                                            <RechartsTooltip content={<MetricsTooltip />} />
                                            <Line
                                                type="monotone"
                                                dataKey="precision"
                                                name="Precision"
                                                stroke={C_PRECISION}
                                                dot={false}
                                                isAnimationActive={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="recall"
                                                name="Recall"
                                                stroke={C_RECALL}
                                                dot={false}
                                                isAnimationActive={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="f_beta_score"
                                                name={`${fBetaLabel} Score`}
                                                stroke={C_FBETA}
                                                dot={false}
                                                isAnimationActive={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="accuracy"
                                                name="Accuracy"
                                                stroke={C_ACCURACY}
                                                dot={false}
                                                isAnimationActive={false}
                                            />
                                            <ReferenceLine
                                                x={round3(optimalThreshold)}
                                                stroke={C_OPTIMAL}
                                                strokeDasharray="4 4"
                                                label={{
                                                    value: 'optimal',
                                                    position: 'insideBottom',
                                                    fill: C_OPTIMAL,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                }}
                                            />
                                            {!isAtOptimal && (
                                                <ReferenceLine
                                                    x={round3(value)}
                                                    stroke={C_BEST}
                                                    strokeDasharray="4 4"
                                                />
                                            )}
                                        </LineChart>
                                    )}
                                </ResponsiveContainer>
                            </div>
                        </div>
                        {chartMode === 'metrics' && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 16,
                                    justifyContent: 'center',
                                    marginTop: 8,
                                }}
                            >
                                {legendItem(C_PRECISION, 'Precision')}
                                {legendItem(C_FBETA, `${fBetaLabel} Score`)}
                                {legendItem(C_RECALL, 'Recall')}
                                {legendItem(C_ACCURACY, 'Accuracy')}
                                {legendItem(C_OPTIMAL, 'Optimal')}
                                {!isAtOptimal && legendItem(C_BEST, 'Current')}
                            </div>
                        )}
                    </div>

                    {/* Current / Optimal threshold cards */}
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ ...statCardStyle, flex: 1, minWidth: 0 }}>
                            <div style={statCardLabelStyle}>Current Threshold</div>
                            <div style={statCardValueStyle}>{round3(value).toFixed(3)}</div>
                            <div style={statCardSubStyle}>
                                {currentCand
                                    ? `Precision: ${f3(currentCand.precision)}, Recall: ${f3(
                                        currentCand.recall
                                    )}, ${fBetaLabel}: ${f3(currentCand.f_beta_score)}`
                                    : '---'}
                            </div>
                        </div>
                        <div style={{ ...statCardStyle, flex: 1, minWidth: 0 }}>
                            <div style={statCardLabelStyle}>{`Optimal ${fBetaLabel} Threshold`}</div>
                            <div style={statCardValueStyle}>
                                {round3(optimalThreshold).toFixed(3)}
                            </div>
                            <div style={statCardSubStyle}>
                                {optimalCand
                                    ? `Precision: ${f3(optimalCand.precision)}, Recall: ${f3(
                                        optimalCand.recall
                                    )}, ${fBetaLabel}: ${f3(optimalCand.f_beta_score)}`
                                    : '---'}
                            </div>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};

export default ThresholdPanel;
